import { arrayMoveImmutable } from "array-move";
import sortBy from "lodash-es/sortBy";
import { IWorkflowNode } from "motw_core/Entity/IWorkflowNode";
import { homeNode } from "motw_core/Repositories/WorkflowRepository";
import * as React from "react";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import Nameof from "source/app/services/NameOf";
import {
    UpdateNodeAsync
} from "source/app/services/Workflows/ChangeData/UpdateNodeAsync";
import IconWithText from "../../../../../../services/Shared/IconWithText/IconWithText";
import {
    GetSortOrderAsync,
    ToggleSortMode,
} from "../../../../../../services/Workflows/SortNodes";
import Render from "../../../../../Shared/Render";
import { GetWorkflowsState } from "../../../Workflows";
import * as styles from "./SortNodes.css";

export function SetSortTarget({
    sourceNode,
    rootNode,
    beforeNode,
    sortMode,
    renderLevel,
}: {
    sourceNode: IWorkflowNode;
    rootNode: IWorkflowNode;
    beforeNode?: IWorkflowNode;
    sortMode: boolean;
    renderLevel: number;
}) {
    return (
        <Render
            id="sortTargetContainer"
            when={ShouldShowSortTarget(sortMode, renderLevel)}
        >
            <Render id="sortTarget" when={SourceHasBeenSet(sourceNode)}>
                <IconWithText
                    iconSuffix={EGlyphicon.save}
                    whenClicked={async () =>
                        await ExecuteSortMoveAsync(
                            sourceNode,
                            rootNode,
                            beforeNode
                        )
                    }
                    displayAsBlock={true}
                    withClass={styles.sortTargetIcon}
                >
                    move here
                </IconWithText>
            </Render>
        </Render>
    );
}

export function SetSortSourceIcon({
    sortMode,
    node,
}: {
    sortMode: boolean;
    node: IWorkflowNode;
}) {
    return (
        <Render id="setSortSource" when={sortMode}>
            <IconWithText
                iconSuffix={EGlyphicon.open}
                whenClicked={() => SetSortSource(node)}
                displayAsBlock={false}
                withClass={styles.sortSourceIcon}
            />
        </Render>
    );
}

export async function GetSortedNodesAsync(
    immediateChildren: IWorkflowNode[],
    rootNode: IWorkflowNode
) {
    if (rootNode.viewchronological) {
        var sortByPropertyName = Nameof<IWorkflowNode>("lastmodifiedtime");
        var chronologicalOrder = sortBy(immediateChildren, sortByPropertyName);
        return chronologicalOrder;
    }

    const existingSortOrder = await GetSortOrderAsync(rootNode);

    if (!existingSortOrder) {
        return immediateChildren;
    }

    let result: IWorkflowNode[] = [];

    const nodesToSort = immediateChildren.filter((node) =>
        existingSortOrder.includes(node.id)
    );

    nodesToSort.sort(
        (a, b) =>
            existingSortOrder.indexOf(a.id) - existingSortOrder.indexOf(b.id)
    );
    result = result.concat(nodesToSort);

    const unsortedNodes = immediateChildren.filter(
        (node) => !existingSortOrder.includes(node.id)
    );

    result = result.concat(unsortedNodes);
    return result;
}

export async function ExecuteSortMoveAsync(
    sourceNode: IWorkflowNode,
    rootNode: IWorkflowNode,
    beforeNode?: IWorkflowNode
) {
    const existingSortOrder = await GetSortOrderAsync(rootNode);

    if (existingSortOrder == null) {
        return;
    }

    if (!existingSortOrder) {
        throw new Error("Cannot determine existing order of nodes");
    }

    const sourceIndex = existingSortOrder.indexOf(sourceNode.id);

    const targetIndex = beforeNode
        ? existingSortOrder.indexOf(beforeNode.id)
        : existingSortOrder.length - 1;

    const newSortOrder = arrayMoveImmutable(
        existingSortOrder,
        sourceIndex,
        targetIndex
    );

    await UpdateNodeAsync(rootNode, { sortorder: newSortOrder });
    ToggleSortMode();
}

const SetSortSource = (sortSource: IWorkflowNode) => {
    const workflows = GetWorkflowsState();
    workflows.setState({ sortSource });
};

const SourceHasBeenSet = (sourceNode: IWorkflowNode) =>
    sourceNode.id !== homeNode.id;

const ShouldShowSortTarget = (sortMode: boolean, renderLevel: number) =>
    sortMode && renderLevel === 0;
